import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download,
  Database,
  ShieldCheck,
  GraduationCap,
  Wrench,
  Headphones,
  Cloud,
  CalendarClock,
  ArrowUpRight,
} from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";
import { cn } from "@/lib/utils";

const SERVICES = [
  {
    Icon: Download,
    title: "Installation",
    short: "On-site & remote installation with license activation, multi-user setup and initial company creation.",
  },
  {
    Icon: Database,
    title: "Data Migration",
    short: "Move from legacy accounting software or older Tally versions with zero data loss and validation.",
  },
  {
    Icon: ShieldCheck,
    title: "AMC",
    short: "Annual maintenance contracts covering updates, patches, support and health checks.",
  },
  {
    Icon: GraduationCap,
    title: "Training",
    short: "Personalised training for finance, purchase and management teams — classroom or remote.",
  },
  {
    Icon: Wrench,
    title: "Customization",
    short: "Custom TDLs, invoice formats, reports and integrations tailored to your workflows.",
  },
  {
    Icon: Headphones,
    title: "Support",
    short: "Priority phone, email and WhatsApp support with strict 2-hour response SLAs.",
  },
  {
    Icon: Cloud,
    title: "Cloud Setup",
    short: "Host TallyPrime securely on cloud with anywhere access, backups and multi-user sync.",
  },
  {
    Icon: CalendarClock,
    title: "Annual Maintenance",
    short: "Preventive audits, upgrades, backup automation and configuration reviews all year long.",
  },
];

export default function Services() {
  const [active, setActive] = useState(0);

  return (
    <section id="services" data-testid="services-section" className="relative py-24 lg:py-32 bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-2xl"
        >
          <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Our Services</motion.span>
          <motion.h2 variants={fadeUp} className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
            End-to-end services.{" "}
            <span className="font-serif-accent text-[#FFA000]">Zero handoffs.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-[17px] text-[#4A5568] max-w-xl leading-relaxed">
            One partner for installation, migration, training, customization and lifetime support.
          </motion.p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left list - ISS style cards */}
          <div className="lg:col-span-5 space-y-2">
            {SERVICES.map((s, i) => (
              <button
                key={s.title}
                onClick={() => setActive(i)}
                data-testid={`service-item-${i}`}
                className={cn(
                  "w-full text-left rounded-xl p-4 flex items-center gap-4 transition-all duration-400 border",
                  active === i
                    ? "bg-[#FFA000] text-white border-transparent shadow-[0_15px_40px_rgba(255,160,0,0.3)]"
                    : "bg-white border-[#0D47A1]/10 hover:bg-[#F5F7FA] hover:border-[#0D47A1]/20"
                )}
              >
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors",
                  active === i ? "bg-white/20" : "bg-[#0D47A1]/8"
                )} style={active === i ? {} : { backgroundColor: "rgba(13,71,161,0.08)" }}>
                  <s.Icon className={cn("w-5 h-5", active === i ? "text-white" : "text-[#0D47A1]")} strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-[16px] tracking-tight">{s.title}</div>
                </div>
                <ArrowUpRight className={cn("w-4 h-4 transition-opacity", active === i ? "opacity-100" : "opacity-30")} />
              </button>
            ))}
          </div>

          {/* Right preview */}
          <div className="lg:col-span-7 relative">
            <div className="sticky top-28 rounded-2xl bg-white border border-[#0D47A1]/10 p-8 lg:p-10 overflow-hidden min-h-[420px] shadow-[0_10px_40px_rgba(13,71,161,0.08)]">
              <div className="orb bg-[#FFA000] w-[300px] h-[300px] -top-24 -right-16 opacity-10" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="relative"
                  data-testid={`service-detail-${active}`}
                >
                  <div className="w-16 h-16 rounded-2xl bg-[#0D47A1] shadow-[0_10px_30px_rgba(13,71,161,0.2)] flex items-center justify-center mb-6">
                    {(() => {
                      const Icon = SERVICES[active].Icon;
                      return <Icon className="w-7 h-7 text-white" strokeWidth={1.75} />;
                    })()}
                  </div>
                  <h3 className="font-display font-black text-3xl lg:text-4xl tracking-tight text-[#1A1A2E]">
                    {SERVICES[active].title}
                  </h3>
                  <p className="mt-4 text-[17px] leading-relaxed text-[#4A5568] max-w-xl">
                    {SERVICES[active].short}
                  </p>

                  <ul className="mt-8 grid grid-cols-2 gap-3">
                    {[
                      "Dedicated engineer",
                      "Same-day onboarding",
                      "Compliance-first setup",
                      "Post-service audit",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-[#1A1A2E]">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFA000]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
